<template>
  <div class="pt-1">
    <pathway-cohort-edit-modal
      :edit-pathway-cohort-active.sync="editPathwayCohortActive"
      :pathway-cohort-data="editPathwayData"
      @refetch-data="refetchData"
      v-if="$can('roster.cohort.update')"
    />

    <!-- <h2 class="content-header-title mb-2">{{ $route.meta.pageTitle }}</h2> -->

    <div class="content-px mb-1">
      <b-row>
        <b-col cols="12" sm="8" md="7">
          <div class="d-flex align-items-center justify-content-start flex-wrap flex-sm-nowrap" style="gap: 0.675rem">
            <b-form-input v-model="searchQuery" class="drop-shadow-default" placeholder="Search..." style="max-width: 28rem" />
            <session-list-filters
              :visibility-filter.sync="visibilityFilter"
              :status-filter.sync="statusFilter"
              :visibility-options="visibilityOptions"
              :status-options="statusOptions"
            />
          </div>
        </b-col>

        <b-col cols="12" sm="4" md="5" class="d-flex align-items-center justify-content-end"> </b-col>
      </b-row>
    </div>

    <!-- Table Container Card -->
    <div no-body class="mb-0 table-no-top">
      <b-table
        ref="refPathwayCohortListTable"
        class="position-relative mb-0"
        :items="fetchPathwayCohorts"
        responsive
        :fields="tableColumnsList"
        primary-key="id"
        :sort-by.sync="sortBy"
        show-empty
        empty-text="No matching records found"
        :sort-desc.sync="isSortDirDesc"
        @row-contextmenu="onRowContextMenu"
      >
        <!-- Column: Session -->
        <template #cell(startDate)="data">
          <div class="d-flex align-items-center" style="gap: 0.625rem">
            <b-avatar
              v-if="data.item.pathway"
              size="36"
              :src="data.item.pathway.avatar"
              :text="avatarText(data.item.pathway.name)"
              :variant="`light-info`"
              :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.id } }"
              rounded
            />
            <div class="d-flex flex-column" style="gap: 0">
              <b-link
                :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.id } }"
                class="font-weight-bold text-nowrap text-white mb-0"
                style="font-size: 1.25rem; line-height: 1.75rem"
              >
                {{ resolvePathwayCohortName(data.item) }}
              </b-link>
              <b-link
                :to="{ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.id } }"
                class="d-flex align-items-center text-muted text-nowrap"
              >
                <lucide-icon name="Library" style="width: 15px; height: 15px; margin-right: 3px" />
                <small style="font-size: 1.05rem; line-height: 1.35rem" v-if="data.item.pathway"> {{ data.item.pathway.name }}</small>
              </b-link>
            </div>
          </div>
        </template>

        <!-- Column: Sections -->
        <template #cell(sections)="data">
          <div class="d-flex align-items-center" style="gap: 0.625rem">
            <div v-for="section in data.value" v-if="section">
              <div v-if="section.course" class="d-flex flex-column">
                <b-avatar
                  size="32"
                  :src="section.course.avatar"
                  :text="avatarText(section.course.name)"
                  :variant="`light-info`"
                  :to="{ name: 'apps-roster-course-section-view', params: { id: section.id } }"
                  class="align-self-center"
                  rounded
                />
                <span class="text-muted text-center" style="font-size: 0.9rem">{{
                  resolveCourseSectionDateFormat(section.startDate, 'MMM Do')
                }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Column: Total Students -->
        <template #cell(profiles)="data">
          <div
            class="text-nowrap"
            :style="data.item.statistics.counts.byStatus.enrolled && 'cursor: help'"
            :id="`student-statuses-${data.item.id}`"
          >
            <span
              class="align-text-top text-capitalize"
              :class="data.item.statistics.counts.byStatus.enrolled ? 'text-white' : 'text-muted'"
              >{{
                data.item.statistics.counts.byStatus.enrolled
                  ? `${data.item.statistics.counts.byStatus.enrolled} Student${
                      data.item.statistics.counts.byStatus.enrolled !== 1 ? 's' : ''
                    }`
                  : 'No Students Enrolled'
              }}</span
            >
          </div>

          <student-list-statuses v-if="data.value" :target-id="`student-statuses-${data.item.id}`" :data="data.item.statistics" />
        </template>

        <!-- Column: Visibility -->
        <template #cell(visibility)="data">
          <b-badge :variant="`light-${resolvePathwayCohortVisibilityVariant(data.item.visibility)}`" class="text-capitalize">
            {{ data.item.visibility.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Status -->
        <template #cell(status)="data">
          <b-badge :variant="`${resolvePathwayCohortStatusVariant(data.item.status)}`" class="text-capitalize">
            {{ data.item.status.split('_').join(' ') }}
          </b-badge>
        </template>

        <!-- Column: Actions -->
        <template #cell(actions)="data">
          <b-dropdown variant="primary" class="dropdown-edit-button">
            <template #button-content>
              <div class="p-50">
                <feather-icon icon="MoreHorizontalIcon" size="21" />
              </div>
            </template>

            <b-dropdown-item v-if="$can('roster.cohort.update')" @click="editPathwayCohort(data.item)">
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>Edit</span>
                <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('roster.cohort.get')"
              @click="$router.push({ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.id } })"
            >
              <div class="d-flex align-items-center justify-content-between w-100">
                <span>View</span>
                <feather-icon icon="EyeIcon" size="15" class="ml-50 text-muted" />
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </template>
      </b-table>
      <div class="mx-2 mb-50">
        <b-row>
          <b-col cols="12" md="3" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="mb-0 text-muted">{{ dataMeta.of }} Total Entries</span>
          </b-col>
          <!-- Pagination -->
          <b-col cols="12" md="9" class="d-flex align-items-center justify-content-center justify-content-sm-end mt-1 mt-sm-0">
            <div class="mr-2 vue-select-up">
              <span class="mb-0 mr-75">Rows Per Page</span>
              <v-select
                v-model="perPage"
                :options="perPageOptions"
                :clearable="false"
                class="per-page-selector d-inline-block"
                :open="true"
              />
            </div>
            <span class="mr-2 ml-lg-50 mb-0">Page {{ currentPage }} of {{ totalPageCount }}</span>
            <b-pagination
              v-model="currentPage"
              :total-rows="totalPathwayCohorts"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-sm-0 ml-lg-50"
              prev-class="prev-item"
              next-class="next-item"
            >
              <template #prev-text>
                <feather-icon icon="ChevronLeftIcon" size="18" />
              </template>
              <template #next-text>
                <feather-icon icon="ChevronRightIcon" size="18" />
              </template>
            </b-pagination>
          </b-col>
        </b-row>
      </div>
    </div>

    <vue-context ref="pathwayCohortListMenu" class="mw-8" v-slot="{ data }">
      <li v-if="data">
        <a
          v-if="$can('roster.cohort.update')"
          class="d-flex align-items-center justify-content-between"
          @click="editPathwayCohort(data.item)"
        >
          <span>Edit</span>
          <feather-icon icon="Edit3Icon" size="15" class="ml-50 text-muted" />
        </a>
        <a
          v-if="$can('roster.cohort.get')"
          class="d-flex align-items-center justify-content-between"
          @click="$router.push({ name: 'apps-roster-pathway-cohort-view', params: { id: data.item.id } })"
        >
          <span>View</span>
          <feather-icon icon="EyeIcon" size="15" class="ml-50 text-muted" />
        </a>
      </li>
    </vue-context>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BTooltip,
} from 'bootstrap-vue';
import vSelect from 'vue-select';
import store from '@/store';
import { ref, onUnmounted } from '@vue/composition-api';
import { avatarText } from '@core/utils/filter';
import SessionListFilters from './PathwayCohortListFilters.vue';
import usePathwayCohorts from '../usePathwayCohorts';
import rosterStoreModule from '../../../../../../store/roster/rosterStoreModule';
import PathwayCohortEditModal from '@/views/apps/roster/pathways/cohorts/edit/PathwayCohortEditModal.vue';
import VueContext from 'vue-context';
import router from '@/router';
import useCourseSections from '../../../courses/sections/useCourseSections';
import StudentListStatuses from '../view/components/StudentListStatuses.vue';

export default {
  components: {
    PathwayCohortEditModal,

    SessionListFilters,
    StudentListStatuses,

    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BTooltip,

    vSelect,
    VueContext,
  },
  watch: {
    '$route.meta.kind'() {
      this.typeFilter = this.$route.meta.kind;
    },
  },
  methods: {
    // archivePathwayCohort(sessionId) {
    //   this.$swal({
    //     title: 'Are you sure?',
    //     text: 'Are you sure you want to archive this session?',
    //     icon: 'warning',
    //     showCancelButton: true,
    //     confirmButtonText: 'Remove',
    //     customClass: {
    //       confirmButton: 'btn btn-primary',
    //       cancelButton: 'btn btn-outline-danger ml-1',
    //     },
    //     buttonsStyling: false,
    //   }).then(async (result) => {
    //     if (result.value) {
    //       store
    //         .dispatch('rosterStoreModule/updateSession', {
    //           sessionId,
    //           sessionData: { status: 'archived' },
    //         })
    //         .then((response) => {
    //           this.refetchData();
    //         });
    //     }
    //   });
    // },
    editPathwayCohort(data) {
      this.editPathwayData = data;
      this.editPathwayCohortActive = true;
    },
    onRowContextMenu(item, index, event) {
      this.$refs.pathwayCohortListMenu.open(event, { item });
      event.preventDefault();
    },
  },
  setup() {
    const editPathwayData = ref({});
    const editPathwayCohortActive = ref(false);

    const isAddNewSessionSidebarActive = ref(false);

    const {
      fetchPathwayCohorts,
      tableColumnsList,
      perPage,
      currentPage,
      totalPathwayCohorts,
      totalPageCount,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPathwayCohortListTable,
      refetchData,

      statusOptions,
      visibilityOptions,

      // UI
      resolvePathwayCohortName,
      resolvePathwayCohortVisibilityVariant,
      resolvePathwayCohortStatusVariant,

      typeFilter,

      // Extra Filters
      visibilityFilter,
      statusFilter,
    } = usePathwayCohorts();

    const { resolveCourseSectionDateFormat } = useCourseSections();

    typeFilter.value = router.currentRoute.meta.kind;

    return {
      // Sidebar
      isAddNewSessionSidebarActive,

      fetchPathwayCohorts,
      tableColumnsList,
      perPage,
      currentPage,
      totalPathwayCohorts,
      totalPageCount,
      dataMeta,
      perPageOptions,
      searchQuery,
      sortBy,
      isSortDirDesc,
      refPathwayCohortListTable,
      refetchData,

      // Filter
      avatarText,

      // UI
      resolvePathwayCohortName,
      resolveCourseSectionDateFormat,
      resolvePathwayCohortVisibilityVariant,
      resolvePathwayCohortStatusVariant,

      visibilityOptions,
      statusOptions,

      typeFilter,

      // Extra Filters
      visibilityFilter,
      statusFilter,

      editPathwayData,
      editPathwayCohortActive,
    };
  },
  metaInfo() {
    return {
      title: `Pathway Cohorts`,
    };
  },
};
</script>

<style lang="scss" scoped>
.per-page-selector {
  width: 90px;
}
</style>
